import ms from "ms";
import { inject, injectable } from "inversify";
import type { SelectedContactType } from "@whatslly/types/SelectedContactType";
import { backgroundClient } from "@whatslly/commons/background/client";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { ChunkedQueue } from "@whatslly/commons/chunkedQueue";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { webScriptBackend } from "./webScriptBackend";
import { DEFAULT_SCRAPER_SETTINGS } from "./whatsapp/messageScraper";
import type { EmittedMessage, IMessageEmitter, IMessageEmitterInstance, SerializedMessageBase } from "@whatslly/types/instant-messaging/messagesEmitter";
import { InjectMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import { IFraudCheckedMessagesSchema } from "@whatslly/server-commons/database/entities/fraud-checked-messages";
import { action, makeObservable, observable, runInAction, type ObservableMap } from "mobx";
import type { IDOMInjectorService } from "@whatslly/instant-messaging/commons/IDOMInjectorService";
import { FRAUD_DOM_CLASSNAMES, IDOMInjectorServiceSymbol } from "@whatslly/instant-messaging/commons/IDOMInjectorService";
import omit from "lodash/omit";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
import { widgetClient } from "@whatslly/commons/widgetClient";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";

type ServiceState = {
  selectedContact: SelectedContactType;
};

type MessageFraudDetectorQueueItem = SerializedMessageBase & {
  contactPhone: string | undefined;
};

const MINIMUM_FRAUD_RATE_TO_HIDE = 80;
const REALTIME_FLUSH_DEBOUNCE_MS = 100;
const MAX_TRACKED_CONTACTS = 50;

@injectable()
export class MessageFraudDetectorService {
  private scraper?: IMessageEmitterInstance;
  private abortController?: AbortController;
  private trackedMessageIds = observable.map<string, ObservableMap<string, IFraudCheckedMessagesSchema | null>>();
  private readonly logger: FrontLogger;
  private selectors: IMSelectorsUnion | undefined;
  private selectedContact?: SelectedContactType;
  fraudMessagesToShow = observable.set<string>();
  private flushDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private flushInFlight: Promise<void> | undefined;
  private fraudObserver: MutationObserver | undefined;
  private fraudDelegatedClickHandler: ((e: MouseEvent) => void) | undefined;
  private hiddenMessageIds = new Set<string>();

  public sendQueue = new ChunkedQueue<MessageFraudDetectorQueueItem>(
    async (messages) => {
      const userPhone = this.MessageEmitter.getMe()?.phone;
      const userName = this.MessageEmitter.getMe()?.name;
      if (!userPhone || messages.length === 0) return;

      const contactPhone = messages[0].contactPhone;
      if (!contactPhone) return;

      const filteredMessages = messages
        .map((message) => ({ id: message.id, text: message.text }))
        .filter((message): message is { id: string; text: string } => message.text !== undefined);

      if (filteredMessages.length === 0) return;

      const res = await backgroundClient.send(BgCommands.RUN_FRAUD_DETECTION, {
        phoneNumber: contactPhone,
        messages: filteredMessages,
        channel: undefined,
        isGroup: this.selectedContact?.isGroup,
        senderPhone: this.selectedContact?.phone,
        senderName: this.selectedContact?.name,
        recipientPhone: userPhone,
        recipientName: userName,
      });

      if (!res) return;

      const highScoreMessages = res.filter((m) => this.isMessageFraudulent(m));
      if (highScoreMessages.length > 0) {
        const selectedPhone = this.selectedContact?.phone;
        const responsePhones = highScoreMessages.map((m) => m.phoneNumber);
        const phonesMatch = responsePhones.every((p) => p === selectedPhone);
        this.logger.warn(
          { count: highScoreMessages.length, selectedPhone, responsePhones, phonesMatch, detections: highScoreMessages.map((m) => m.detections) },
          "[FRAUD] High score messages received"
        );
      }
      res.forEach((message) => this.updateTrackedMessageIds(message));
      this.instrumentDOM();
    },
    { chunkSize: 20, interval: ms("30s") }
  );

  private flushSoon(delayMs: number = REALTIME_FLUSH_DEBOUNCE_MS) {
    if (this.flushDebounceTimer) {
      clearTimeout(this.flushDebounceTimer);
    }
    this.flushDebounceTimer = setTimeout(() => {
      void this.flushNow();
    }, delayMs);
  }

  private async flushNow(): Promise<void> {
    if (this.flushInFlight) return this.flushInFlight;

    this.flushInFlight = this.sendQueue
      .flush()
      .catch((err) => {
        this.logger.error({ err }, "[FRAUD] sendQueue.flush failed");
      })
      .finally(() => {
        this.flushInFlight = undefined;
      });

    return this.flushInFlight;
  }

  updateTrackedMessageIds(message: IFraudCheckedMessagesSchema) {
    const phoneNumberMessages = this.trackedMessageIds.get(message.phoneNumber);
    if (!phoneNumberMessages) {
      this.trackedMessageIds.set(message.phoneNumber, observable.map<string, IFraudCheckedMessagesSchema | null>([[message.messageId, message]]));
      return;
    }
    phoneNumberMessages?.set(message.messageId, message);
  }

  constructor(
    @inject(FrontLogger) private frontLogger: FrontLogger,
    @inject(InjectMessageEmitter) private readonly MessageEmitter: IMessageEmitter<unknown, unknown>,
    @inject(IDOMInjectorServiceSymbol) private readonly domInjectorService: IDOMInjectorService
  ) {
    this.logger = this.frontLogger.getLogger("message-fraud-detector-service");
    makeObservable<MessageFraudDetectorService, "selectedContact" | "trackMessage" | "updateTrackedMessageIds">(this, {
      selectedContact: observable,
      trackMessage: action,
      updateTrackedMessageIds: action,
    });
  }

  get messagesToHide(): string[] {
    if (!this.selectedContact?.phone) return [];
    const phoneMessages = this.trackedMessageIds.get(this.selectedContact.phone);
    if (!phoneMessages) return [];
    const result: string[] = [];
    for (const [messageId, message] of phoneMessages) {
      if (this.fraudMessagesToShow.has(messageId)) continue;
      if (this.isMessageFraudulent(message)) {
        result.push(messageId);
      }
    }
    return result;
  }

  public showFraudMessage(messageId: string) {
    if (!messageId) return;
    this.domInjectorService.revealFraudMessage(messageId, this.selectors);
    this.fraudMessagesToShow.add(messageId);
    this.instrumentDOM();
  }

  private resolveHiddenMessageIdFromElement(el: Element | null | undefined, selectorAttr: string): string | undefined {
    let current: Element | null = el ?? null;
    while (current) {
      const id = current.getAttribute?.(selectorAttr);
      if (id && this.hiddenMessageIds.has(id)) return id;
      current = current.parentElement;
    }
    return undefined;
  }

  private startFraudDomHooks(selectors: IMSelectorsUnion): void {
    if (!this.fraudDelegatedClickHandler) {
      const selectorAttr = selectors.domListener.threadElementSelector;

      this.fraudDelegatedClickHandler = async (e: MouseEvent) => {
        const masked = (e.target as Element | null)?.closest(`.${FRAUD_DOM_CLASSNAMES.FRAUD_MESSAGE}`);
        if (!masked) return;
        const messageId = this.resolveHiddenMessageIdFromElement(masked, selectorAttr);
        if (!messageId) return;

        e.preventDefault();
        e.stopPropagation();

        const shouldReveal = await widgetClient.send(WidgetCommands.OPEN_SHOW_FRAUD_DETECTED_MODAL, messageId);
        if (shouldReveal) {
          this.showFraudMessage(messageId);
        }
      };

      document.addEventListener("click", this.fraudDelegatedClickHandler, true);
    }

    if (!this.fraudObserver) {
      const messagesContainer = document.querySelector<HTMLElement>(selectors.css.messagesContainer);
      if (!messagesContainer) return;

      const selectorAttr = selectors.domListener.threadElementSelector;

      this.fraudObserver = new MutationObserver((mutations) => {
        if (this.hiddenMessageIds.size === 0) return;

        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;

            const closestId = this.resolveHiddenMessageIdFromElement(node, selectorAttr);
            if (closestId) {
              this.domInjectorService.hideFraudMessage(closestId, selectors);
              continue;
            }

            const wrappers = node.querySelectorAll<HTMLElement>(`[${selectorAttr}]`);
            for (const wrapper of wrappers) {
              const id = wrapper.getAttribute(selectorAttr);
              if (!id || !this.hiddenMessageIds.has(id)) continue;
              this.domInjectorService.hideFraudMessage(id, selectors);
            }
          }
        }
      });

      this.fraudObserver.observe(messagesContainer, { childList: true, subtree: true });
    }
  }

  private stopFraudDomHooks(): void {
    if (this.fraudObserver) {
      this.fraudObserver.disconnect();
      this.fraudObserver = undefined;
    }
    if (this.fraudDelegatedClickHandler) {
      document.removeEventListener("click", this.fraudDelegatedClickHandler, true);
      this.fraudDelegatedClickHandler = undefined;
    }
  }

  instrumentDOM(singleMessageId?: string) {
    if (!this.selectors) return;
    const selectors = this.selectors;

    const nextHidden = new Set(this.messagesToHide);
    if (singleMessageId && !this.fraudMessagesToShow.has(singleMessageId)) {
      nextHidden.add(singleMessageId);
    }
    this.hiddenMessageIds = nextHidden;

    for (const messageId of this.hiddenMessageIds) {
      this.domInjectorService.hideFraudMessage(messageId, selectors);
    }

    this.startFraudDomHooks(selectors);
  }

  private async emitMessage(message: EmittedMessage, selectedContact: Pick<SelectedContactType, "phone">) {
    if (!this.selectors) return;

    const messageToSend = omit(message, ["loadMedia", "loadSignedMedia"]);

    void (async () => {
      await this.sendQueue.enqueue({
        ...messageToSend,
        id: message.serializedMessageId,
        contactPhone: selectedContact?.phone,
      });
      this.flushSoon();
    })();
    void this.trackMessage(message.serializedMessageId);
  }

  private isMessageFraudulent(message: IFraudCheckedMessagesSchema | null): boolean {
    if (!message || !message.detections) return false;
    const urlScore = message.detections.url ?? 0;
    return urlScore >= MINIMUM_FRAUD_RATE_TO_HIDE;
  }

  resetState({ selectedContact }: Partial<ServiceState> = {}) {
    this.abortController?.abort();
    this.abortController = undefined;
    this.scraper = undefined;
    this.fraudMessagesToShow = observable.set<string>();
    this.hiddenMessageIds.clear();
    this.stopFraudDomHooks();
    this.domInjectorService.clearFraudMessageStyles();
    if (this.flushDebounceTimer) {
      clearTimeout(this.flushDebounceTimer);
      this.flushDebounceTimer = undefined;
    }

    this.pruneTrackedContacts();

    if (!selectedContact || !this.selectors) {
      return;
    }

    this.MessageEmitter.inject(this.selectors);
    if (this.MessageEmitter.getContactPhone(this.MessageEmitter.getActiveChat()) !== selectedContact.phone) {
      this.logger.warn("selected contact changed during fraud check");
      return;
    }

    this.listenToMessages(selectedContact);
  }

  private pruneTrackedContacts() {
    if (this.trackedMessageIds.size <= MAX_TRACKED_CONTACTS) return;

    const keysToRemove = Array.from(this.trackedMessageIds.keys()).slice(0, this.trackedMessageIds.size - MAX_TRACKED_CONTACTS);
    keysToRemove.forEach((key) => this.trackedMessageIds.delete(key));
  }

  private async getCheckedMessages(): Promise<ObservableMap<string, IFraudCheckedMessagesSchema | null> | undefined> {
    if (!this.selectedContact || !this.selectedContact.phone) return observable.map();
    const selectedContactPhone = this.selectedContact.phone;
    if (this.trackedMessageIds.has(selectedContactPhone)) {
      const existingMessages = this.trackedMessageIds.get(selectedContactPhone);
      this.instrumentDOM();
      return existingMessages;
    }
    const res = await backgroundClient.send(BgCommands.GET_FRAUD_DETECTION_CHECKED_MESSAGES, {
      phoneNumber: selectedContactPhone,
    });
    if (!res) {
      runInAction(() => {
        this.trackedMessageIds.set(selectedContactPhone, observable.map<string, IFraudCheckedMessagesSchema | null>());
      });
      return this.trackedMessageIds.get(selectedContactPhone);
    }
    runInAction(() => {
      this.trackedMessageIds.set(
        selectedContactPhone,
        observable.map<string, IFraudCheckedMessagesSchema | null>(res.map((message) => [message.messageId, message]))
      );
    });
    this.instrumentDOM();
    return this.trackedMessageIds.get(selectedContactPhone);
  }

  private async listenToMessages(selectedContact: SelectedContactType) {
    runInAction(() => {
      this.selectedContact = selectedContact;
    });
    const chatModel = this.MessageEmitter.getActiveChat();
    if (!chatModel) return;

    const checkedMessages = await this.getCheckedMessages();

    this.abortController = new AbortController();
    this.scraper = new this.MessageEmitter(
      chatModel,
      {
        listenToNewMessages: true,
        mediaMessageTypes: DEFAULT_SCRAPER_SETTINGS.mediaMessageTypes,
        vcardMessageTypes: DEFAULT_SCRAPER_SETTINGS.vcardMessageTypes,
        abortSignal: this.abortController.signal,
      },
      this.logger
    );
    for await (const message of this.scraper) {
      if (!this.selectedContact?.phone || !checkedMessages || message.isSentByMe) {
        continue;
      }
      const syncedMessageLastUpdate = checkedMessages.get(message.serializedMessageId)?.updatedAt;
      const isMessageSynced = checkedMessages.has(message.serializedMessageId);
      const isMessageOutdated = syncedMessageLastUpdate && Number(syncedMessageLastUpdate) < message.latestUpdate;

      if (!isMessageSynced || isMessageOutdated) {
        void this.emitMessage(message, selectedContact);
      }
    }
  }

  private async trackMessage(messageId: string) {
    if (!messageId || this.trackedMessageIds.get(this.selectedContact?.phone || "")?.has(messageId)) {
      return;
    }
    this.trackedMessageIds.get(this.selectedContact?.phone || "")?.set(messageId, null);
  }

  init({ selectors }: { selectors: IMSelectorsUnion }) {
    this.selectors = selectors;

    webScriptBackend.on(WebScriptCommands.LOGOUT, () => {
      this.resetState(undefined);
    });
  }
}
